import {ISymbolStateConfig, StateReelSymbol} from "@nolimitcity/slot-game/bin/core/reelsymbol/StateReelSymbol";
import {SpinReelController} from "@nolimitcity/slot-game/bin/game/reel/spinreel/SpinReelController";
import {ParsedGameData} from "@nolimitcity/slot-game/bin/core/server/data/ParsedGameData";
import {BookOfShadowsGameMode} from "../gameMode/BookOfShadowsGameMode";
import {BookOfShadowsParsedGameData} from "../server/data/BookOfShadowsParsedGameData";
import {ReelAnimationState} from "@nolimitcity/slot-game/bin/core/reel/reel/ReelAnimationState";
import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {GameModeEvent} from "@nolimitcity/slot-game/bin/core/gamemode/event/GameModeEvent";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import {BookOfShadowsGameConfig} from "../BookOfShadowsGameConfig";
import {Reels} from "@nolimitcity/slot-game/bin/core/reel/reelarea/Reels";
import {ScalableLayer} from "@nolimitcity/slot-game/bin/core/stage/ScalableLayer";
import {StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {Helper} from "@nolimitcity/slot-game/bin/core/utils/Helper";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {IReelStartSpinData} from "@nolimitcity/slot-game/bin/core/reel/reel/ReelController";
import {TweenLite} from "gsap";
import {SoundManager} from "../sound/SoundManager";
import {GameSetting} from "@nolimitcity/slot-game/bin/core/setting/GameSetting";

export class BookOfShadowsReelController extends SpinReelController {
    private _fsSpecialSymbol: string;
    private _sGlowRuneLayer: ScalableLayer;
    private _sGlowContainer: PIXI.Container;
    private _scatterPositions: number[] | undefined;

    /**
     * @constructs
     * @param reelId
     */
    constructor(reelId: number) {
        super(reelId);

        this._sGlowRuneLayer = StageManager.getLayer(BookOfShadowsGameConfig.instance.LAYERS.S_GLOW_RUNE.name);

        this._sGlowContainer = new PIXI.Container();
        this._sGlowRuneLayer.addChild(this._sGlowContainer);
    }

    /**
     * @description To re-order the symbols so that S,M1 and M2 stays on top.
     */
    public reOrderSymbols() {
        let sym: StateReelSymbol;
        const symbols: string[] = ["M2", "M1", "S"];

        symbols.forEach((value: string) => {
            for (let i = 0; i < this._stopReelSet.length; i++) {
                if (this._stopReelSet[i] === value) {
                    sym = this.findSymbol(i - 1)!;
                    sym && this.addChildOnView(sym);
                }
            }
        });
    }

    /**
     * @description To update the data whenever required.
     * @param data
     */
    public updateGameData(data: ParsedGameData): void {
        this._gameMode = data.mode;
        this._nextGameMode = data.nextMode;
        this._stopReelSet = data.reels[this._reelId];
    }

    /**
     * @inheritDoc
     * @override #33 -> Implementing S glow runes
     * @param data
     * @param previousDelay
     */
    public startSpin(data: IReelStartSpinData, previousDelay: number): number {
        if (BookOfShadowsGameConfig.instance.LOCKED_REEL[this._reelId] === "0") {
            this._sGlowContainer.removeChildren();
        }

        return super.startSpin(data, previousDelay);
    }

    /**
     * @description To fade away the glowing runes from the scatters which are drawn to centre, it will always be the last item in the container.
     * @param duration
     */
    public fadeAwaySGlowRunes(duration: number): void {
        const item: PIXI.DisplayObject = this._sGlowContainer.children[this._sGlowContainer.children.length - 1];
        TweenLite.to(item, duration, {
            alpha: 0,
            onComplete: () => {
                this._sGlowContainer.removeChild(item);
            }
        });
    }

    /**
     * @inheritDoc
     * @override Storing fsSpecialSymbol.
     * @param data
     */
    public onGameDataParsed(data: BookOfShadowsParsedGameData): void {
        this._fsSpecialSymbol = data.fsSpecialSymbol;
        this._scatterPositions = data.scatterPositions;
        super.onGameDataParsed(data);
    }

    /**
     * @description To changes the state of scatter symbol w.r.t. anticipation.
     * @param state
     */
    public changeStateForScatter(state: ISymbolStateConfig): void {
        let sym: StateReelSymbol;

        for (let i = (5 - BookOfShadowsGameConfig.instance.ACTIVE_ROWS) + 1; i < this._stopReelSet.length - 1; i++) {
            if (this._stopReelSet[i] === "S") {
                sym = this.findSymbol(i - 1)!;
                if (sym) {
                    sym.changeState({
                        state: state,
                        fadeInDuration: 0.3
                    });
                }
            }
        }
    }

    // #108
    public exitFS(): void {
        this._sGlowContainer.removeChildren();

        this.applySGlowRune(true);
    }

    /**
     * @inheritDoc
     * @override To re-order the symbols.
     */
    protected initReelStrip(): void {
        super.initReelStrip();
        if (!this._reelInitialized) {
            return;
        }

        this.reOrderSymbols();
        this.applySGlowRune(true);
    }

    /**
     * @inheritDoc
     * @override To replace the symbol with gold version of that symbol in FS. // #22
     * @param previousSymbol
     * @param init
     */
    protected createNextSymbol(previousSymbol?: StateReelSymbol | null, init?: boolean): StateReelSymbol {
        let retVal: StateReelSymbol = super.createNextSymbol(previousSymbol, init),
            state: ISymbolStateConfig;

        if (retVal.symName !== "S") {
            if (((this._gameMode.indexOf(BookOfShadowsGameMode.FREESPIN) !== -1) || (this._gameMode.indexOf(BookOfShadowsGameMode.NORMAL) === -1 && this._nextGameMode.indexOf(BookOfShadowsGameMode.FREESPIN) !== -1))
                && (this._fsSpecialSymbol === retVal.symName)) {
                state = BookOfShadowsGameConfig.instance.SYMBOL_STATES.goldNormal;
                if (retVal.currentState?.keyword === ReelAnimationState.SPIN) {
                    state = BookOfShadowsGameConfig.instance.SYMBOL_STATES.goldSpin;
                }

                retVal.changeState({
                    state: state,
                    setStack: true
                })
            }
        }

        return retVal;
    }

    /**
     * @inheritDoc
     */
    protected addEventListeners(): void {
        super.addEventListeners();

        EventHandler.addEventListener(this, GameModeEvent.GAME_MODE_TRANSITION_PREPARE, (event: GameEvent) => this.onModeChange(event.params[0], event.params[1]));
    }

    /**
     * @inheritDoc
     * @override #33 -> Implementing S glow runes
     */
    protected onSpinStopStarted(): void {
        this.applySGlowRune(false);

        super.onSpinStopStarted();
    }

    /**
     * @description To handle mode change event.
     * @param fromMode
     * @param nextMode
     */
    private onModeChange(fromMode: string, nextMode: string): void {
        if (fromMode === BookOfShadowsGameMode.FREESPIN && nextMode !== BookOfShadowsGameMode.FREESPIN) {
            this._fsSpecialSymbol = "";
        }
    }

    /**
     * @description To apply the scatter glow runes on the reel background.
     */
    private applySGlowRune(init: boolean): void {
        const len: number = 5,
            duration: number = 0.05,
            rowDiff: number = (5 - BookOfShadowsGameConfig.instance.ACTIVE_ROWS);

        let sym: StateReelSymbol,
            pos: number[],
            item: PIXI.Sprite;

        for (let i = 0; i < len; i++) {
            sym = Reels.getSymbol(this.reelId, i)!;
            if (sym && sym.symName === "S") {
                item = new PIXI.Sprite(GameResources.getTextures("sGlowRune")[0]);
                item.anchor.set(0.5);
                item.blendMode = PIXI.BLEND_MODES.ADD;

                pos = Helper.getSymbolPositions(this.reelId, i);
                item.position.set(pos[0], pos[1]);
                item.alpha = 0;
                TweenLite.to(item, duration, {alpha: 1});

                this._sGlowContainer.addChild(item);

                BookOfShadowsGameConfig.instance.SCATTER_REEL_ID[this._reelId] = true;
                BookOfShadowsGameConfig.instance.SCATTER_REEL_SYM_ID[this._reelId] = i;

                // shouldn't count scatters in case of init.
                if (init) {
                    continue;
                }

                if (i >= rowDiff) {
                    SoundManager.playScatterLanding(BookOfShadowsGameConfig.instance.SCATTER_COUNT);
                    BookOfShadowsGameConfig.instance.SCATTER_COUNT++;
                    if (BookOfShadowsGameConfig.instance.SCATTER_COUNT > 2) {
                        BookOfShadowsGameConfig.instance.SCATTER_COUNT = 2;

                        // #189
                        let symbol: StateReelSymbol;
                        const duration: number = GameSetting.isFastSpin ? 0.2 : 0.4;
                        if (this._scatterPositions) {
                            for (let j: number = 0; j <= this._reelId; j++) {
                                if (this._scatterPositions[j] !== -1) {
                                    symbol = Reels.getSymbol(j, this._scatterPositions[j])!;
                                    TweenLite.to(symbol.scale, duration, {x: 1.3, y: 1.3});
                                    TweenLite.to(symbol.scale, duration, {x: 1, y: 1, delay: duration});
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}